import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import styled from "@emotion/styled";
import { Accordion, AccordionItem } from "@szhsin/react-accordion";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";

type SelectedCategory = {
  categoryCode: number; // 선택한 카테고리의 실제 코드
  depth: number; // 선택한 뎁스 (1~2)
  path: string[]; // 경로명 배열 ['여성', '상의']
  codes: number[]; // 경로 코드 배열 [1, 11]
};

type Props = {
  selectedCategories: SelectedCategory[];
  setSelectedCategories: React.Dispatch<
    React.SetStateAction<SelectedCategory[]>
  >;
};

const CategoryDropdown = ({
  selectedCategories,
  setSelectedCategories,
}: Props) => {
  const theme = useTheme();

  // 열려있는 카테고리를 추적하는 상태 (드롭다운 네비게이션용)
  const [depth1, setDepth1] = useState<number | null>(null);
  const [depth2, setDepth2] = useState<number | null>(null);

  // console.log("selectedCategories", selectedCategories);

  const { getCategoryList } = useApiRequest();

  const { data: depth1List } = useQuery({
    queryKey: ["depth1List"],
    queryFn: () => getCategoryList(),
  });

  const { data: depth2List } = useQuery({
    queryKey: ["depth2List", depth1],
    queryFn: () => getCategoryList(depth1),
    enabled: !!depth1,
  });

  // 카테고리가 선택되어 있는지 확인하는 함수
  const isSelected = (categoryCode: number): boolean => {
    return selectedCategories.some((selectedItem) => {
      // selectedItem.codes 배열에 현재 categoryCode가 포함되어 있는지 확인
      return (
        selectedItem.codes.includes(categoryCode) ||
        selectedItem.codes.includes(categoryCode)
      );
    });
  };

  // 현재 네비게이션 경로를 가져오는 함수
  const getCurrentPath = (
    depth: number,
  ): { codes: number[]; names: string[] } => {
    const codes: number[] = [];
    const names: string[] = [];

    if (depth >= 1 && depth1) {
      codes.push(depth1);
      const item1 = depth1List?.find((item: any) => item.code === depth1);
      if (item1) names.push(item1.name_ko);
    }

    if (depth >= 2 && depth2) {
      codes.push(depth2);
      const item2 = depth2List?.find((item: any) => item.code === depth2);
      if (item2) names.push(item2.name_ko);
    }

    return { codes, names };
  };

  // 카테고리 선택/해제 토글 함수
  const toggleCategorySelection = (
    categoryCode: number,
    depth: number,
    itemName: string,
  ) => {
    const isCurrentlySelected = isSelected(categoryCode);

    if (isCurrentlySelected) {
      // 선택 해제
      setSelectedCategories((prev) =>
        prev.filter((item) => item.categoryCode !== categoryCode),
      );
    } else {
      // 선택 추가
      const { codes, names } = getCurrentPath(depth - 1); // 선택한 depth 전까지의 경로

      // 선택한 아이템 정보 추가
      const finalCodes = [...codes, categoryCode];
      const finalNames = [...names, itemName];

      const newSelectedCategory: SelectedCategory = {
        categoryCode,
        depth,
        path: finalNames,
        codes: finalCodes,
      };

      setSelectedCategories((prev) => [...prev, newSelectedCategory]);
    }
  };

  // 아코디언 열고 닫기 함수들
  const toggleDepth1 = (e: React.MouseEvent, item: any) => {
    setDepth1((prevDepth1) => {
      const newDepth1 = prevDepth1 === item.code ? null : item.code;

      // 다른 depth1 카테고리로 변경될 때 하위 네비게이션 초기화
      if (newDepth1 !== null && newDepth1 !== prevDepth1) {
        setDepth2(null);
      }

      return newDepth1;
    });
  };

  const toggleDepth2 = (e: React.MouseEvent, item: any) => {
    setDepth2((prevDepth2) => {
      return prevDepth2 === item.code ? null : item.code;
    });
  };

  // 카테고리 선택 핸들러들
  const handleSelectDepth1 = (e: React.MouseEvent, item: any) => {
    e.preventDefault();
    e.stopPropagation();
    toggleCategorySelection(item.code, 1, item.name_ko);
  };

  const handleSelectDepth2 = (e: React.MouseEvent, item: any) => {
    e.preventDefault();
    e.stopPropagation();
    toggleCategorySelection(item.code, 2, item.name_ko);
  };

  return (
    <Container>
      <CategoryList>
        <StyledAccordion>
          {depth1List?.map((depth1Item: any) => (
            <AccordionItem
              className={"one"}
              key={depth1Item.code}
              itemKey={depth1Item.code}
              header={
                <AccordionHeader onClick={(e) => toggleDepth1(e, depth1Item)}>
                  <ItemName
                    onClick={(e) => handleSelectDepth1(e, depth1Item)}
                    active={isSelected(depth1Item.code)}>
                    {depth1Item.name_ko}
                  </ItemName>

                  <ArrowButton>
                    {!!depth1Item.has_children && (
                      <Image
                        src={
                          depth1Item.code === depth1
                            ? theme.image.arrow_up.default
                            : theme.image.arrow_down.default
                        }
                      />
                    )}
                  </ArrowButton>
                </AccordionHeader>
              }>
              <StyledAccordion>
                {depth2List?.map((depth2Item: any) => (
                  <AccordionItem
                    key={depth2Item.code}
                    itemKey={depth2Item.code}
                    className={"two"}
                    header={
                      <AccordionHeader
                        onClick={(e) => toggleDepth2(e, depth2Item)}>
                        <ItemName
                          onClick={(e) => handleSelectDepth2(e, depth2Item)}
                          active={isSelected(depth2Item.code)}>
                          {depth2Item.name_ko}
                        </ItemName>
                        <ArrowButton>
                          {!!depth2Item.has_children && (
                            <Image
                              src={
                                depth2Item.code === depth2
                                  ? theme.image.arrow_up.default
                                  : theme.image.arrow_down.default
                              }
                            />
                          )}
                        </ArrowButton>
                      </AccordionHeader>
                    }>
                    <></>
                  </AccordionItem>
                ))}
              </StyledAccordion>
            </AccordionItem>
          ))}
        </StyledAccordion>
      </CategoryList>
    </Container>
  );
};

export default CategoryDropdown;

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2.4rem",
});

const CategoryList = styled.div({
  display: "flex",
  flexDirection: "column",
});

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  button: { width: "100%" },
  ".two": {
    paddingLeft: "2rem",
  },
  ".szh-accordion__item-content": {
    transition: "height 0.25s cubic-bezier(0, 0, 0, 1)",
  },
}));

const AccordionHeader = styled.div(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "2.4rem 2rem",
  borderBottom: `1px solid ${theme.color.border_gray}`,
  transition: "color 0.2s ease",
}));

const ItemName = styled.div<{ active?: boolean }>(({ theme, active }) => ({
  cursor: "pointer",
  fontSize: "1.4rem",
  textAlign: "start",
  fontWeight: theme.fontWeight.regular,
  whiteSpace: "nowrap",
  color: active ? theme.color.green : theme.color.black,
}));

const ArrowButton = styled.div({
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});
